class ProductBundleToggle extends HTMLElement {
  constructor() {
    super();
    this.boundVariantChange = this.handleVariantChange.bind(this);
    this.boundLinkClick = this.handleLinkClick.bind(this);
    this.boundLinkKeydown = this.handleLinkKeydown.bind(this);
    this.abortController = null;
    this.lastVariantId = null;
    this.isNavigating = false;
  }

  connectedCallback() {
    this.sectionElement = this.closest('[data-section-id]');
    this.sectionId =
      this.dataset.sectionId || (this.sectionElement && this.sectionElement.getAttribute('data-section-id'));
    this.sectionRoot = this.closest('.shopify-section') || this.sectionElement;

    this.addEventListener('click', this.boundLinkClick);
    this.addEventListener('keydown', this.boundLinkKeydown);
    this.isVariantListener = this.isPrimaryInstance();

    if (this.sectionId && this.isVariantListener) {
      document.addEventListener('theme:variant:change', this.boundVariantChange);
    }
  }

  disconnectedCallback() {
    if (this.sectionId && this.isVariantListener) {
      document.removeEventListener('theme:variant:change', this.boundVariantChange);
    }
    this.removeEventListener('click', this.boundLinkClick);
    this.removeEventListener('keydown', this.boundLinkKeydown);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  getSectionToggles() {
    if (!this.sectionId || !this.dataset.productId) {
      return [];
    }

    const selector = `product-bundle-toggle[data-product-id="${this.dataset.productId}"][data-section-id="${this.sectionId}"]`;
    const scope = this.sectionRoot || document;
    return Array.from(scope.querySelectorAll(selector));
  }

  isPrimaryInstance() {
    const toggles = this.getSectionToggles();
    if (!toggles.length) {
      return true;
    }

    const desktopToggle = toggles.find((toggle) => toggle.dataset.productBundleTogglePosition === 'desktop');
    return (desktopToggle || toggles[0]) === this;
  }

  handleLinkKeydown(event) {
    const link = event.target.closest('[data-product-bundle-toggle-link]');
    if (!link || !this.contains(link)) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }

    event.preventDefault();
    link.click();
  }

  handleLinkClick(event) {
    const link = event.target.closest('[data-product-bundle-toggle-link]');
    if (!link || !this.contains(link)) {
      return;
    }

    if (this.isNavigating) {
      event.preventDefault();
      return;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    const href = link.getAttribute('href');
    if (!href) {
      return;
    }

    event.preventDefault();
    this.isNavigating = true;

    const activeItem = this.querySelector('.product-bundle-toggle__item--active');
    if (activeItem) {
      activeItem.classList.add('product-bundle-toggle__item--fade-out');
    }

    link.classList.add('product-bundle-toggle__item--fade-in');

    window.setTimeout(() => {
      window.location.assign(href);
    }, 180);
  }

  async handleVariantChange(event) {
    if (!this.sectionId || !this.isVariantListener) {
      return;
    }

    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) {
      return;
    }

    const eventSection = eventTarget.closest('[data-section-id]');
    const eventSectionId = eventSection && eventSection.getAttribute('data-section-id');
    if (eventSectionId !== this.sectionId) {
      return;
    }

    const variant = event.detail && event.detail.variant;
    const variantId = variant && variant.id;

    if (!variantId || variantId === this.lastVariantId) {
      return;
    }

    const colorOptionPosition = Number.parseInt(this.dataset.currentColorOptionPosition || '', 10);
    if (!colorOptionPosition) {
      return;
    }

    const colorOptionKey = `option${colorOptionPosition}`;
    const nextColorValue = String(variant[colorOptionKey] || '')
      .trim()
      .toLowerCase();
    const currentColorValue = String(this.dataset.currentColorValue || '')
      .trim()
      .toLowerCase();

    if (!nextColorValue || nextColorValue === currentColorValue) {
      return;
    }

    this.lastVariantId = variantId;

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const productUrl = this.dataset.productUrl || window.location.pathname;
    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', 'product-bundle-toggle-render');
    requestUrl.searchParams.set('variant', String(variantId));

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: this.abortController.signal,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        return;
      }

      const responseHtml = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
      const toggles = this.getSectionToggles();
      const replacementTemplate = responseDocument.querySelector(
        `product-bundle-toggle[data-product-id="${this.dataset.productId}"]`
      );

      if (!replacementTemplate) {
        return;
      }

      toggles.forEach((toggle) => {
        const togglePosition = toggle.dataset.productBundleTogglePosition || 'desktop';
        const replacement = replacementTemplate.cloneNode(true);
        replacement.dataset.sectionId = this.sectionId;
        replacement.dataset.productBundleTogglePosition = togglePosition;
        toggle.replaceWith(replacement);
      });
    } catch (error) {
      // ignore errors
    } finally {
      this.abortController = null;
    }
  }
}

if (!customElements.get('product-bundle-toggle')) {
  customElements.define('product-bundle-toggle', ProductBundleToggle);
}
